import axios from 'axios';
import * as R from 'ramda';
import urlTemplate from 'url-template';
import { API } from './';

/**
 * Inject params into url
 * @param {string} url - parametrized resource url, e.g: /suggester-api/v5/position/{locale}/{positionId}
 * @param {Object} urlParams - values for the params, e.g: { locale: 'en', positionId: 1234 }
 * @returns {string} - url
 */
const createUrl = (url, urlParams) => {
  if (R.isEmpty(urlParams)) return url;

  return urlTemplate.parse(url).expand(urlParams);
};

/**
 * Simple fetching service
 * @param {string} resourceName - name of the entity to fetch
 * @param {Object} urlParams - parameters to be injected into the url
 * @param {Object} params - parameters to be sent in the url query (e.g: for ?id=12345 it will be: { id: 12345 })
 * @param {Object} data - data to be sent as the request body, only applicable for 'PUT', 'POST', and 'PATCH'
 * @param {Object} transformArgs - params of the transform function
 * @returns {Promise<never>|Promise<AxiosResponse<any>>}
 */
interface Options {
  params?: Record<string, string>;
  data?: any;
  transformArgs?: Record<string, any>;
}

const callAPI = (
  resourceName: keyof typeof API,
  urlParams: Record<string, string> = {},
  { params, data, transformArgs }: Options = {}
) => {
  const resource = API[resourceName];

  if (!resource) return Promise.reject(new Error('no matching resource'));

  const { method = 'get', url, transform } = resource;

  return axios({
    url: createUrl(url, urlParams),
    method,
    params,
    data,
  })
    .then(R.prop('data'))
    .then(response => {
      if (
        !transform ||
        typeof transform !== 'function' ||
        response == null ||
        R.isEmpty(response)
      ) {
        return Promise.resolve(response);
      }

      return Promise.resolve(transform({ response, transformArgs }));
    })
    .catch(err => Promise.reject(err));
};

export default callAPI;
